import useSeo from "@/hooks/useSeo";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import NewsletterForm from "@/components/feature/NewsletterForm";
import Container from "@/components/base/Container";
import SectionHeading from "@/components/base/SectionHeading";
import { milestones, principles, siteInfo, testimonials } from "@/mocks/site";

export default function AboutPage() {
  useSeo({
    title: "关于我们 | 咖位观察局的定位与内容原则-咖位观察局",
    description:
      "了解咖位观察局：一个专注网红明星跨界对比的内容观察站，坚持维度先于结论、数据可追溯、拒绝拉踩情绪，用可复核的分析解读咖位差距与热度走势。",
    keywords: "关于咖位观察局,网红明星观察,咖位方法论,内容原则,跨界对比",
    type: "website",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "AboutPage",
          name: "关于我们",
          description: "咖位观察局的品牌定位、内容原则与联系方式。",
          inLanguage: "zh-CN",
        },
        {
          "@type": "Organization",
          name: siteInfo.name,
          email: siteInfo.email,
          foundingDate: siteInfo.founded,
          slogan: siteInfo.tagline,
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "/" },
            { "@type": "ListItem", position: 2, name: "关于我们" },
          ],
        },
      ],
    },
  });

  return (
    <>
      <SiteHeader />
      <main className="pt-16 md:pt-20">
        <Breadcrumbs items={[{ label: "首页", path: "/" }, { label: "关于我们" }]} />

        <section className="border-b border-background-200/80 bg-background-100 py-12 md:py-16">
          <Container>
            <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-14">
              <div>
                <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.22em] text-primary-600">
                  <span className="h-px w-6 bg-primary-500" />
                  ABOUT US
                </span>
                <h1 className="mt-4 font-heading text-3xl font-bold leading-snug text-foreground-950 md:text-4xl">
                  关于咖位观察局
                </h1>
                <p className="mt-5 text-sm leading-relaxed text-foreground-600 md:text-[15px]">
                  {siteInfo.description}
                </p>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-[15px]">
                  我们相信，娱乐话题并不等于没有逻辑。只要把评价标准说清楚、把时间范围标明白，一张合影、一场直播、一次代言官宣，都能读出一套可以讨论的位置关系。
                </p>
              </div>
              <div className="h-64 w-full overflow-hidden rounded-lg md:h-80">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20editorial%20workspace%20scene%20with%20a%20round%20wooden%20table%2C%20several%20open%20notebooks%20and%20a%20brass%20magnifier%2C%20soft%20amber%20afternoon%20light%20through%20linen%20curtains%2C%20deep%20crimson%20ceramic%20cup%2C%20no%20people%2C%20refined%20magazine%20still%20life%20photography&width=1200&height=900&seq=kawei-about-01&orientation=landscape"
                  alt="咖位观察局内容团队工作场景"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
          </Container>
        </section>

        <section className="bg-background-50 py-16 md:py-20">
          <Container>
            <SectionHeading
              eyebrow="POSITIONING"
              title="我们的定位与发展理念"
              description="不做情绪放大器，做有耐心的解读者。这是我们从开始到现在没有改过的一条线。"
            />
            <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
              <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 text-background-50">
                  <i className="ri-compass-3-line text-xl" />
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">定位</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  面向泛娱乐读者、内容从业者与品牌市场，提供结构化的跨界对比与热度观察基础信息。
                </p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-500 text-secondary-950">
                  <i className="ri-line-chart-line text-xl" />
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">理念</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  位置是流动的，所以我们记录趋势而不是下终审判决，给每一条结论都标上时间刻度。
                </p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-500 text-background-50">
                  <i className="ri-shield-check-line text-xl" />
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">边界</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  不传播未经核实的私人信息，不针对个人做人身评价，讨论对象始终是行业现象与位置关系。
                </p>
              </div>
            </div>
          </Container>
        </section>

        <section className="border-y border-background-200/80 bg-background-100 py-16 md:py-20">
          <Container>
            <SectionHeading
              eyebrow="PRINCIPLES"
              title="四条内容原则"
              description="这四条既是我们的工作方法，也是我们邀请读者一起监督的承诺。"
            />
            <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
              {principles.map((item, index) => (
                <div key={item.title} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-6">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 font-heading text-sm font-bold text-primary-700">
                    0{index + 1}
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </Container>
        </section>

        <section className="bg-background-50 py-16 md:py-20">
          <Container>
            <SectionHeading eyebrow="MILESTONES" title="一路走来的几个节点" />
            <ol className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
              {milestones.map((item) => (
                <li key={item.year} className="rounded-lg border border-background-200 bg-background-100 p-6">
                  <span className="font-heading text-2xl font-bold text-primary-600">{item.year}</span>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-600">{item.event}</p>
                </li>
              ))}
            </ol>
          </Container>
        </section>

        <section className="border-y border-background-200/80 bg-background-100 py-16 md:py-20">
          <Container>
            <SectionHeading
              eyebrow="TESTIMONIALS"
              title="读者怎么说"
              description="来自品牌、创作与影视一线读者的真实反馈，是我们持续更新的动力。"
            />
            <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
              {testimonials.map((item) => (
                <figure key={item.name} className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-6">
                  <span className="flex h-8 w-8 items-center justify-center text-accent-500">
                    <i className="ri-double-quotes-l text-2xl" />
                  </span>
                  <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                    {item.content}
                  </blockquote>
                  <figcaption className="mt-5 border-t border-background-200 pt-4">
                    <p className="text-sm font-medium text-foreground-900">{item.name}</p>
                    <p className="mt-0.5 text-xs text-foreground-500">{item.role}</p>
                  </figcaption>
                </figure>
              ))}
            </div>
          </Container>
        </section>

        <section id="subscribe" className="bg-primary-950 py-16 md:py-20">
          <Container>
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
              <div>
                <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.22em] text-accent-300">
                  <span className="h-px w-6 bg-accent-400" />
                  CONTACT
                </span>
                <h2 className="mt-4 font-heading text-2xl font-bold leading-snug text-background-50 md:text-[2rem]">
                  联系我们
                </h2>
                <p className="mt-4 max-w-lg text-sm leading-relaxed text-background-200/85">
                  选题线索、内容指正、商务合作都欢迎。留下邮箱即可，我们会定期查看每一条反馈。
                </p>
                <ul className="mt-6 space-y-3 text-sm text-background-100/90">
                  <li className="flex items-center gap-3">
                    <span className="flex h-5 w-5 items-center justify-center text-accent-400">
                      <i className="ri-mail-line text-lg" />
                    </span>
                    读者来信：{siteInfo.email}
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="flex h-5 w-5 items-center justify-center text-accent-400">
                      <i className="ri-briefcase-line text-lg" />
                    </span>
                    商务合作：{siteInfo.mediaEmail}
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="flex h-5 w-5 items-center justify-center text-accent-400">
                      <i className="ri-time-line text-lg" />
                    </span>
                    {siteInfo.updateFrequency}
                  </li>
                </ul>
              </div>
              <div className="rounded-lg border border-background-50/15 bg-background-50/5 p-6 md:p-8">
                <NewsletterForm tone="light" />
              </div>
            </div>
          </Container>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}